<script lang="ts">
  import { getGameIconUrl, getPassportStackGames, passportGames } from './games';

  let { referer, label }: { referer?: string; label: string } = $props();

  const stackGames = $derived(getPassportStackGames(referer));
  const hiddenGamesCount = $derived(passportGames.length - stackGames.length);

  let showGameList = $state(false);
  let gameStackWrap = $state<HTMLElement>();
  let gameList = $state<HTMLElement>();
  let gameListPosition = $state({ left: 0, top: 0, arrowLeft: 0, above: false });

  function updateGameListPosition() {
    if (!gameStackWrap || !gameList || typeof window === 'undefined') return;

    const anchor = gameStackWrap.getBoundingClientRect();
    const list = gameList.getBoundingClientRect();
    const edgePadding = 16;
    const left = Math.min(
      Math.max(anchor.left + anchor.width / 2 - list.width / 2, edgePadding),
      window.innerWidth - list.width - edgePadding
    );
    const belowTop = anchor.bottom + 11;
    const aboveTop = anchor.top - list.height - 11;
    const above =
      belowTop + list.height > window.innerHeight - edgePadding && aboveTop >= edgePadding;
    const top = above
      ? aboveTop
      : Math.max(edgePadding, Math.min(belowTop, window.innerHeight - list.height - edgePadding));
    const arrowLeft = Math.min(
      Math.max(anchor.left + anchor.width / 2 - left, edgePadding),
      list.width - edgePadding
    );

    gameListPosition = { left, top, arrowLeft, above };
  }

  $effect(() => {
    if (!showGameList || !gameStackWrap || !gameList || typeof document === 'undefined') return;
    const currentGameStackWrap = gameStackWrap;

    const dismissOnOutsideClick = (event: PointerEvent) => {
      if (!event.composedPath().includes(currentGameStackWrap)) showGameList = false;
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        showGameList = false;
        currentGameStackWrap.querySelector('button')?.focus();
      }
    };

    updateGameListPosition();
    document.addEventListener('pointerdown', dismissOnOutsideClick);
    document.addEventListener('keydown', dismissOnEscape);
    window.addEventListener('resize', updateGameListPosition);
    window.addEventListener('scroll', updateGameListPosition, true);

    return () => {
      document.removeEventListener('pointerdown', dismissOnOutsideClick);
      document.removeEventListener('keydown', dismissOnEscape);
      window.removeEventListener('resize', updateGameListPosition);
      window.removeEventListener('scroll', updateGameListPosition, true);
    };
  });
</script>

<span
  bind:this={gameStackWrap}
  class:open={showGameList}
  class:above={gameListPosition.above}
  class="passport-offer__game-stack-wrap"
  role="group"
  onmouseenter={() => (showGameList = true)}
  onmouseleave={() => (showGameList = false)}
  onfocusin={() => (showGameList = true)}
>
  <button
    class="passport-offer__game-stack"
    type="button"
    aria-label={label}
    aria-expanded={showGameList}
    aria-controls="passport-game-list"
    onclick={() => (showGameList = !showGameList)}
  >
    {#each stackGames as game (game.name)}
      <img src={getGameIconUrl(game.name)} alt="" loading="lazy" />
    {/each}
    <span class="passport-offer__game-stack-more">+{hiddenGamesCount}</span>
  </button>
  <span
    bind:this={gameList}
    id="passport-game-list"
    class="passport-offer__game-list"
    role="tooltip"
    aria-hidden={!showGameList}
    style:left={`${gameListPosition.left}px`}
    style:top={`${gameListPosition.top}px`}
    style:--game-list-arrow-left={`${gameListPosition.arrowLeft}px`}
  >
    <span class="passport-offer__game-list-items">
      {#each passportGames as game (game.name)}
        <span class="passport-offer__game-list-item">
          <img src={getGameIconUrl(game.name)} alt="" loading="lazy" />
          {game.name}
        </span>
      {/each}
    </span>
  </span>
</span>

<style>
  .passport-offer__game-stack {
    display: inline-flex;
    position: relative;
    z-index: 1;
    align-items: center;
    border: 0;
    border-radius: 999px;
    padding: 0;
    color: inherit;
    background: transparent;
    cursor: pointer;
    font: inherit;
    line-height: 1;
    vertical-align: -0.25em;
    margin-left: 0.3rem;
  }

  .passport-offer__game-stack-wrap {
    position: relative;
    display: inline-flex;
    vertical-align: middle;
  }

  .passport-offer__game-stack:hover,
  .passport-offer__game-stack:focus-visible {
    outline: 3px solid var(--passport-focus-ring);
    outline-offset: 3px;
  }

  .passport-offer__game-stack img {
    width: 1.15rem;
    height: 1.15rem;
  }

  .passport-offer__game-stack img + img {
    margin-left: -0.45rem;
  }

  .passport-offer__game-stack-more {
    display: inline-flex;
    width: 1.15rem;
    height: 1.15rem;
    align-items: center;
    justify-content: center;
    margin-left: -0.45rem;
    color: var(--passport-stack-more-ink);
    background: var(--passport-stack-more-surface);
    border: 1px solid var(--passport-stack-more-border);
    border-radius: 50%;
    font-size: 0.5rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  .passport-offer__game-list {
    position: fixed;
    z-index: 2;
    display: grid;
    width: min(17rem, calc(100vw - 2rem));
    gap: 0.55rem;
    padding: 0.75rem;
    color: var(--passport-ink);
    background: var(--passport-paper);
    border: 1px solid var(--passport-border);
    border-radius: 0.8rem;
    box-shadow: 0 0.7rem 1.5rem rgba(23, 35, 61, 0.18);
    font-size: var(--passport-type-micro);
    opacity: 0;
    pointer-events: none;
    transform: translateY(-0.25rem);
    transition:
      opacity 140ms ease,
      transform 140ms ease;
  }

  .passport-offer__game-list::before {
    position: absolute;
    top: -0.35rem;
    left: var(--game-list-arrow-left);
    width: 0.65rem;
    height: 0.65rem;
    background: var(--passport-paper);
    border-top: 1px solid var(--passport-border);
    border-left: 1px solid var(--passport-border);
    content: '';
    transform: translateX(-50%) rotate(45deg);
  }

  .passport-offer__game-stack-wrap.above .passport-offer__game-list::before {
    top: auto;
    bottom: -0.35rem;
    border-top: 0;
    border-right: 1px solid var(--passport-border);
    border-bottom: 1px solid var(--passport-border);
    border-left: 0;
  }

  .passport-offer__game-stack-wrap:hover .passport-offer__game-list,
  .passport-offer__game-stack-wrap:focus-within .passport-offer__game-list,
  .passport-offer__game-stack-wrap.open .passport-offer__game-list {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }

  .passport-offer__game-stack-wrap.above .passport-offer__game-list {
    transform: translateY(0.25rem);
  }

  .passport-offer__game-list-items {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.4rem 0.65rem;
  }

  .passport-offer__game-list-item {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.3rem;
    line-height: 1.15;
  }

  .passport-offer__game-list-item img {
    flex: none;
    width: 1.15rem;
    height: 1.15rem;
  }

  @media (prefers-reduced-motion: reduce) {
    .passport-offer__game-list {
      transition: none;
    }
  }
</style>
